/**
 * DRBFM Table Component
 *
 * Displays DRBFM analysis data in a tabular format.
 * Supports three data types:
 * - Design Changes
 * - Failure Modes
 * - Countermeasures
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Typography,
  Tooltip,
  IconButton,
  Collapse,
  Link as MuiLink,
  ButtonBase,
} from '@mui/material';
import {
  KeyboardArrowDown as ExpandIcon,
  KeyboardArrowUp as CollapseIcon,
  ThreeDRotation as ThreeDIcon,
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { DesignChange, FailureMode, Countermeasure, KnowledgeReference } from '../../types';
import { translateWithFallback } from '../../utils/translation';

// Type definitions for table data
type TableDataType = 'designChanges' | 'failureModes' | 'countermeasures';

interface DRBFMTableProps {
  type: TableDataType;
  data: DesignChange[] | FailureMode[] | Countermeasure[];
  /**
   * DRBFM session id used for the 3D navigation. When omitted the
   * 3D action column is hidden for failure modes (TG #673).
   */
  drbfmSessionId?: string;
  selectedFailureModeId?: string;
  onSelectFailureMode?: (failureMode: FailureMode) => void;
}

// Severity color mapping
const getSeverityColor = (severity: string | undefined): 'error' | 'warning' | 'info' | 'success' => {
  switch (severity?.toLowerCase()) {
    case 'critical':
      return 'error';
    case 'high':
      return 'warning';
    case 'medium':
      return 'info';
    case 'low':
      return 'success';
    default:
      return 'info';
  }
};

// Countermeasure type color mapping
const getCountermeasureTypeColor = (type: string | undefined): 'primary' | 'secondary' | 'info' => {
  switch (type?.toLowerCase()) {
    case 'prevention':
      return 'primary';
    case 'detection':
      return 'secondary';
    case 'mitigation':
      return 'info';
    default:
      return 'info';
  }
};

const getKnowledgeReferenceTitle = (ref: KnowledgeReference): string => (
  ref.fileName || ref.title || ref.documentId
);

const getKnowledgeReferencePath = (ref: KnowledgeReference): string => (
  `/documents/${encodeURIComponent(ref.documentId)}`
);

const formatKnowledgeReferenceSnippet = (snippet?: string): string => (
  (snippet || '').replace(/<\/?(?:em|mark)\b[^>]*>/gi, '')
);

const tableMinWidthByType: Record<TableDataType, number> = {
  designChanges: 820,
  failureModes: 1120,
  countermeasures: 720,
};

const longTextCellSx = {
  maxWidth: 260,
  whiteSpace: 'normal',
  lineHeight: 1.6,
};

// Expandable row for Design Changes
const DesignChangeRow: React.FC<{ row: DesignChange }> = ({ row }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton
            size="small"
            onClick={() => setOpen(!open)}
            aria-label={
              open
                ? t('drbfm.cadLink.designChangeCollapse', '設計変更詳細を閉じる')
                : t('drbfm.cadLink.designChangeExpand', '設計変更詳細を開く')
            }
          >
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Chip label={row.category} size="small" variant="outlined" />
        </TableCell>
        <TableCell>{row.itemName}</TableCell>
        <TableCell>
          <Tooltip title={row.changeDescription || ''}>
            <Typography variant="body2" noWrap sx={{ maxWidth: 300 }}>
              {row.changeDescription}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          {row.impactAreas?.map((area, idx) => (
            <Chip key={idx} label={area} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
          ))}
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={5}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('drbfm.table.specifications')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('drbfm.table.oldSpec')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.oldSpecification}
                  </Typography>
                </Paper>
                <Paper variant="outlined" sx={{ flex: 1, p: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('drbfm.table.newSpec')}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {row.newSpecification}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Expandable row for Failure Modes
const FailureModeRow: React.FC<{
  row: FailureMode;
  drbfmSessionId?: string;
  selected?: boolean;
  onSelect?: (failureMode: FailureMode) => void;
}> = ({
  row,
  drbfmSessionId,
  selected = false,
  onSelect,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const linkedCadModelId = row.cadAnchor?.cadModelId;
  const canView3D = Boolean(linkedCadModelId && drbfmSessionId);

  const handleSelect = () => {
    onSelect?.(row);
  };

  const handleToggleOpen = () => {
    setOpen(!open);
    handleSelect();
  };

  const handleView3D = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!canView3D) return;
    const params = new URLSearchParams();
    params.set('cad_model', linkedCadModelId!);
    params.set('drbfm_session', drbfmSessionId!);
    // `failure_mode` lets the viewer open the detail drawer directly for the
    // clicked row instead of just showing every pin (TG #673).
    params.set('failure_mode', row.id);
    navigate(`/cad-viewer?${params.toString()}`);
  };

  return (
    <>
      <TableRow hover selected={selected}>
        <TableCell>
          <IconButton
            size="small"
            onClick={handleToggleOpen}
            aria-label={
              open
                ? t('drbfm.cadLink.failureModeCollapse', '故障モード詳細を閉じる')
                : t('drbfm.cadLink.failureModeExpand', '故障モード詳細を開く')
            }
          >
            {open ? <CollapseIcon /> : <ExpandIcon />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Tooltip title={row.failureDescription || ''}>
            <ButtonBase
              onClick={handleSelect}
              disableRipple
              aria-label={translateWithFallback(t, 'drbfm.cadLink.selectFailureModeLabel', `故障モードを選択: ${row.failureDescription}`, {
                description: row.failureDescription,
              })}
              sx={{
                display: 'block',
                width: '100%',
                textAlign: 'left',
                borderRadius: 0.5,
                p: 0.25,
              }}
            >
              <Typography variant="body2" sx={longTextCellSx}>
                {row.failureDescription}
              </Typography>
            </ButtonBase>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={row.failureCause || ''}>
            <Typography variant="body2" sx={longTextCellSx}>
              {row.failureCause}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Tooltip title={row.failureEffect || ''}>
            <Typography variant="body2" sx={longTextCellSx}>
              {row.failureEffect}
            </Typography>
          </Tooltip>
        </TableCell>
        <TableCell>
          <Chip
            label={row.severity}
            color={getSeverityColor(row.severity)}
            size="small"
          />
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: (row.severityScore || 0) >= 8 ? 'error.main' : 'text.primary',
            }}
          >
            {row.severityScore || '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="body2"
            sx={{
              fontWeight: 'bold',
              color: (row.rpnScore || 0) >= 100 ? 'error.main' : 'text.primary',
            }}
          >
            {row.rpnScore || '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Tooltip
            title={
              canView3D
                ? t('drbfm.cadLink.view3DTooltip', '3Dビューアで該当部位を表示')
                : t('drbfm.cadLink.view3DUnavailable', 'CADモデルが紐付いていない、またはセッションIDが未指定')
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={!canView3D}
                onClick={handleView3D}
                aria-label={t('drbfm.cadLink.view3DLabel', '3Dで見る')}
              >
                <ThreeDIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              {row.knowledgeReferences && row.knowledgeReferences.length > 0 && (
                <>
                  <Typography variant="subtitle2" gutterBottom>
                    {t('drbfm.knowledge.references')}
                  </Typography>
                  {row.knowledgeReferences.map((ref, idx) => {
                    const referenceTitle = getKnowledgeReferenceTitle(ref);
                    const snippet = formatKnowledgeReferenceSnippet(ref.snippet);

                    return (
                      <Paper key={idx} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                        <MuiLink
                          component={RouterLink}
                          to={getKnowledgeReferencePath(ref)}
                          variant="subtitle2"
                          underline="hover"
                        >
                          {referenceTitle}
                        </MuiLink>
                        {ref.fileName && ref.fileName !== referenceTitle && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            {ref.fileName}
                          </Typography>
                        )}
                        {snippet && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}
                          >
                            {snippet}
                          </Typography>
                        )}
                        <Box sx={{ mt: 1 }}>
                          <Chip
                            label={`${t('drbfm.knowledge.relevance')}: ${Math.round((ref.relevanceScore || 0) * 100)}%`}
                            size="small"
                          />
                        </Box>
                      </Paper>
                    );
                  })}
                </>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

// Countermeasure row
const CountermeasureRow: React.FC<{ row: Countermeasure }> = ({ row }) => {
  const { t } = useTranslation();

  return (
    <TableRow hover>
      <TableCell>
        <Chip
          label={t(`drbfm.countermeasureType.${row.countermeasureType?.toLowerCase()}`)}
          color={getCountermeasureTypeColor(row.countermeasureType)}
          size="small"
        />
      </TableCell>
      <TableCell>
        <Tooltip title={row.description || ''}>
          <Typography variant="body2" sx={{ maxWidth: 400 }}>
            {row.description}
          </Typography>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Typography
          variant="body2"
          sx={{
            fontWeight: 'bold',
            color: (row.effectivenessScore || 0) >= 8 ? 'success.main' : 'text.primary',
          }}
        >
          {row.effectivenessScore || '-'}
        </Typography>
      </TableCell>
      <TableCell align="center">
        <Chip
          label={`P${row.priority || '-'}`}
          color={row.priority === 1 ? 'error' : row.priority === 2 ? 'warning' : 'default'}
          size="small"
        />
      </TableCell>
    </TableRow>
  );
};

const DRBFMTable: React.FC<DRBFMTableProps> = ({
  type,
  data,
  drbfmSessionId,
  selectedFailureModeId,
  onSelectFailureMode,
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Get paginated data
  const paginatedData = data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Empty state
  if (data.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography color="text.secondary">
          {t('drbfm.table.noData')}
        </Typography>
      </Box>
    );
  }

  // Render table based on type
  const renderTable = () => {
    switch (type) {
      case 'designChanges':
        return (
          <Table size="small" sx={{ minWidth: tableMinWidthByType.designChanges }}>
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell>{t('drbfm.table.category')}</TableCell>
                <TableCell>{t('drbfm.table.itemName')}</TableCell>
                <TableCell>{t('drbfm.table.changeDescription')}</TableCell>
                <TableCell>{t('drbfm.table.impactAreas')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <DesignChangeRow key={(row as DesignChange).id} row={row as DesignChange} />
              ))}
            </TableBody>
          </Table>
        );

      case 'failureModes':
        return (
          <Table
            size="small"
            sx={{ minWidth: tableMinWidthByType.failureModes, tableLayout: 'fixed' }}
          >
            <TableHead>
              <TableRow>
                <TableCell width={50} />
                <TableCell width={220}>{t('drbfm.table.failureDescription')}</TableCell>
                <TableCell width={260}>{t('drbfm.table.failureCause')}</TableCell>
                <TableCell width={260}>{t('drbfm.table.failureEffect')}</TableCell>
                <TableCell width={110}>{t('drbfm.table.severity')}</TableCell>
                <TableCell align="center" width={80}>{t('drbfm.table.severityScore')}</TableCell>
                <TableCell align="center" width={80}>{t('drbfm.table.rpnScore')}</TableCell>
                <TableCell align="center" width={64}>3D</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <FailureModeRow
                  key={(row as FailureMode).id}
                  row={row as FailureMode}
                  drbfmSessionId={drbfmSessionId}
                  selected={(row as FailureMode).id === selectedFailureModeId}
                  onSelect={onSelectFailureMode}
                />
              ))}
            </TableBody>
          </Table>
        );

      case 'countermeasures':
        return (
          <Table size="small" sx={{ minWidth: tableMinWidthByType.countermeasures }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('drbfm.table.countermeasureType')}</TableCell>
                <TableCell>{t('drbfm.table.description')}</TableCell>
                <TableCell align="center">{t('drbfm.table.effectiveness')}</TableCell>
                <TableCell align="center">{t('drbfm.table.priority')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedData.map((row) => (
                <CountermeasureRow key={(row as Countermeasure).id} row={row as Countermeasure} />
              ))}
            </TableBody>
          </Table>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
        {renderTable()}
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={data.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('common.rowsPerPage')}
      />
    </Box>
  );
};

export default DRBFMTable;
